import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Zap, CheckCircle2, TrendingUp, Clock, Brain,
  Code, Target, Rocket, MessageSquare, FileText, Search, Settings
} from 'lucide-react';

const GenerativeAIConsulting = () => {
  const metrics = [
    { value: '3-8 Weeks', label: 'To Production', description: 'From kickoff to live generative AI system' },
    { value: '200+', label: 'AI Projects', description: 'Including LLM and generative AI deployments' },
    { value: '60-80%', label: 'Faster', description: 'Than traditional consulting firms' },
    { value: '99%', label: 'Satisfaction', description: 'Across all generative AI engagements' },
  ];

  const services = [
    {
      icon: Brain,
      title: 'LLM Integration & Custom GPT Deployment',
      description: 'Connect GPT-4, Claude, Gemini, or LLaMA to your internal systems with proper guardrails, cost controls, and business logic.',
      outcomes: [
        'Custom GPT wrappers fine-tuned on your data',
        'Secure internal API integration with access controls',
        'Cost optimization: batching, caching, model routing',
        'Latency benchmarking and production hardening',
      ],
    },
    {
      icon: Search,
      title: 'RAG Systems (Retrieval-Augmented Generation)',
      description: 'Build enterprise knowledge systems that answer questions from your own documents, databases, and internal knowledge bases accurately.',
      outcomes: [
        'Document ingestion pipelines for PDFs, Word, emails',
        'Vector database setup: Pinecone, Weaviate, Chroma',
        'Hybrid search (semantic + keyword) for accuracy',
        'Answer grounding with source citations',
      ],
    },
    {
      icon: MessageSquare,
      title: 'AI Agents & Autonomous Workflows',
      description: 'Deploy multi-step AI agents that can research, reason, and execute tasks across your business systems without human-in-the-loop for each step.',
      outcomes: [
        'LangChain / LangGraph agent architecture',
        'Tool-calling agents connected to APIs and databases',
        'Agentic workflows for research, triage, and reporting',
        'Human-in-the-loop checkpoints for high-stakes decisions',
      ],
    },
    {
      icon: FileText,
      title: 'Document Intelligence & Content Automation',
      description: 'Automate document-heavy processes: contracts, invoices, medical records, compliance reports, and customer communications.',
      outcomes: [
        'Structured data extraction from unstructured documents',
        'Automated report and summary generation',
        'Contract review and clause extraction',
        'Multi-language document processing',
      ],
    },
    {
      icon: Code,
      title: 'Fine-Tuning & Domain-Specific Models',
      description: 'When general models fall short, we fine-tune open-source LLMs on your domain data for higher accuracy, lower cost, and full data control.',
      outcomes: [
        'Fine-tuning on Llama 3, Mistral, Phi-3',
        'Domain-specific training data curation',
        'RLHF and instruction tuning pipelines',
        'On-premise or private cloud deployment',
      ],
    },
    {
      icon: Settings,
      title: 'Generative AI Strategy & Roadmapping',
      description: 'Not sure where to start? We map your business processes to generative AI use cases, rank them by ROI, and build a phased implementation plan.',
      outcomes: [
        'Use case discovery workshop (2-3 days)',
        'Build vs. buy vs. API analysis',
        'Data readiness and security assessment',
        'Phased 90-day implementation roadmap',
      ],
    },
  ];

  const useCases = [
    { industry: 'Healthcare', useCase: 'Clinical note summarization, prior auth drafting, patient Q&A bots' },
    { industry: 'Insurance', useCase: 'Claims triage, policy Q&A, underwriting document extraction' },
    { industry: 'Manufacturing', useCase: 'Maintenance report generation, quality defect analysis, supplier RFQ drafting' },
    { industry: 'Finance', useCase: 'Financial report summarization, regulatory filing assistance, client email drafting' },
    { industry: 'Retail & E-commerce', useCase: 'Product description generation, customer support bots, review analysis' },
    { industry: 'Logistics', useCase: 'Shipment status bots, route optimization commentary, compliance documentation' },
  ];

  const techStack = [
    { category: 'LLMs', items: ['GPT-4o / GPT-4', 'Claude 3.5 Sonnet', 'Gemini Pro', 'Llama 3', 'Mistral'] },
    { category: 'Frameworks', items: ['LangChain', 'LangGraph', 'LlamaIndex', 'Haystack'] },
    { category: 'Vector Databases', items: ['Pinecone', 'Weaviate', 'Chroma', 'pgvector'] },
    { category: 'Cloud', items: ['Azure OpenAI', 'AWS Bedrock', 'Google Vertex AI', 'Self-hosted'] },
  ];

  const faqs = [
    {
      q: 'What is generative AI consulting?',
      a: 'Generative AI consulting is the process of helping businesses identify, design, and implement generative AI systems such as LLM-powered chatbots, RAG knowledge bases, AI agents, and document automation tools in a way that delivers measurable business value.',
    },
    {
      q: 'How long does a generative AI implementation take?',
      a: 'A focused generative AI project typically takes 3-8 weeks with Agility. A RAG system or chatbot can go live in 3-4 weeks. A multi-agent workflow or fine-tuned model deployment typically takes 6-10 weeks. We scope tightly and ship fast.',
    },
    {
      q: 'Do you work with proprietary/confidential data?',
      a: 'Yes. We have extensive experience building systems that keep sensitive data inside your own cloud environment, never sending it to third-party model APIs. We support Azure OpenAI, AWS Bedrock, and self-hosted open-source models for full data sovereignty.',
    },
    {
      q: 'What industries do you serve for generative AI?',
      a: 'Healthcare, insurance, manufacturing, finance, retail, and logistics are our primary verticals. Each has specific compliance and data requirements that we handle as part of the implementation.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Generative AI Consulting & Implementation | RAG Pipelines, AI Agents & LLM Integration | Agility</title>
        <meta
          name="description"
          content="Production-grade Generative AI systems — RAG pipelines, AI agents, content automation, and intelligent workflows. Claude, GPT-4, and open-source models shipped to production in 3–6 weeks."
        />
        <meta
          name="keywords"
          content="generative AI consulting, generative AI consulting company, LLM consulting, RAG implementation, AI agent development, GPT-4 consulting, LLM integration services, generative AI implementation, AI chatbot consulting, document AI"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/generative-ai-consulting" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Generative AI Consulting Company | LLM & RAG Implementation | Agility" />
        <meta property="og:description" content="Generative AI consulting: LLM integration, RAG systems, AI agents, document automation. Production-ready in 3-8 weeks." />
        <meta property="og:url" content="https://www.agilitytech.ai/generative-ai-consulting" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Generative AI Consulting",
            "provider": {
              "@type": "Organization",
              "name": "Agility",
              "url": "https://www.agilitytech.ai"
            },
            "description": "Generative AI consulting services including LLM integration, RAG system development, AI agent deployment, and document intelligence automation.",
            "areaServed": "Worldwide",
            "serviceType": "Generative AI Consulting",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Generative AI Services",
              "itemListElement": [
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "LLM Integration", "description": "Custom LLM integration with GPT-4, Claude, Gemini, and open-source models" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "RAG Systems", "description": "Retrieval-augmented generation systems for enterprise knowledge bases" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI Agents", "description": "Autonomous AI agents for multi-step business workflows" } },
                { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Document Intelligence", "description": "AI-powered document extraction, summarization, and automation" } }
              ]
            },
            "mainEntityOfPage": {
              "@type": "FAQPage",
              "mainEntity": faqs.map(f => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a }
              }))
            }
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-violet-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-500 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/30 rounded-full mb-8">
            <Brain className="w-4 h-4 text-violet-400" />
            <span className="text-violet-300 text-sm font-medium">Generative AI Consulting</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Generative AI Consulting<br />
            <span className="text-teal-400">That Ships to Production</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            We build LLM integrations, RAG knowledge systems, AI agents, and document automation that work in your environment not just in demos.
            Production-ready in 3-8 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Get a Generative AI Roadmap
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              See Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {metrics.map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Generative AI Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From strategy to production deployment every service is scoped for speed and measurable outcomes.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">{s.description}</p>
                  <ul className="space-y-2">
                    {s.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases by Industry */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Generative AI by Industry
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Concrete use cases we've shipped not whitepaper concepts.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((u, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="text-teal-600 font-bold text-sm uppercase tracking-wider mb-2">{u.industry}</div>
                <p className="text-gray-700 text-sm leading-relaxed">{u.useCase}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Stack</h2>
            <p className="text-lg text-gray-600">We work with every major LLM platform and framework model-agnostic, infrastructure-flexible.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">{t.category}</div>
                <ul className="space-y-2">
                  {t.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-800 text-sm font-medium">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Generative AI Consulting Common Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                <p className="text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Services & Resources</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'AI Implementation Services', href: '/ai-implementation-services' },
              { label: 'AI Consulting Ahmedabad', href: '/ai-consulting-company-ahmedabad' },
              { label: 'AI Consulting India', href: '/ai-consulting-india' },
              { label: 'Hire AI Developers', href: '/hire-ai-developers' },
              { label: 'AI for Healthcare', href: '/ai-consulting-healthcare' },
              { label: 'AI for Insurance', href: '/ai-consulting-insurance' },
              { label: 'OpenAI GPT Development', href: '/openai-gpt-development' },
              { label: 'Case Studies', href: '/case-studies' },
            ].map((link, i) => (
              <Link
                key={i}
                to={link.href}
                className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm group"
              >
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-violet-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Ship Generative AI to Production?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us your use case. We'll scope a plan and give you a timeline in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Talk to a Generative AI Consultant
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default GenerativeAIConsulting;
